import React, { useEffect } from 'react'
import { motion } from 'framer-motion'
import { DollarSign, Bitcoin, Briefcase } from 'lucide-react'
import { useInvestmentList } from '../hooks/ListInvestments'
import { InvestmentList, CryptoInvestmentList, TesouroDiretoInvestmentList } from '../models/ModelInvestments'
import AnimatedBackground from '../../utils/AnimatedBackground'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../ui/table";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import TopBar from '../../utils/TopBar';
import Footer from '../../utils/Footer';

const formatNumber = (value: number): string => {
  return value.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

const InvestmentsList: React.FC = () => {
  const {
    investments,
    cryptoInvestments,
    tesouroDiretoInvestments,
    selectedType,
    handleAssetTypeSelection,
    availableTypes,
  } = useInvestmentList()

  useEffect(() => {
    if (!selectedType) {
      handleAssetTypeSelection('stock')
    }
  }, [selectedType, handleAssetTypeSelection])

  const assetTypes = [
    { type: 'stock', icon: DollarSign, label: 'Stocks' },
    { type: 'crypto', icon: Bitcoin, label: 'Crypto Currency' },
    { type: 'tesouro_direto', icon: Briefcase, label: 'Tesouro Direto' },
  ].filter(type => {
    if (type.type === 'crypto') return availableTypes['crypto']
    return availableTypes[type.type]
  })

  const renderInvestmentTable = () => {
    switch (selectedType) {
      case 'stock':
        return (
          <Card className="bg-[#0f1115] border-gray-800">
            <CardHeader>
              <CardTitle className="text-white">Stocks Overview</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table className="text-white">
                  <TableHeader>
                    <TableRow className="border-gray-800">
                      <TableHead className="text-gray-400">Operation</TableHead>
                      <TableHead className="text-gray-400">Country</TableHead>
                      <TableHead className="text-gray-400">Market</TableHead>
                      <TableHead className="text-gray-400">Currency</TableHead>
                      <TableHead className="text-gray-400">Ticker</TableHead>
                      <TableHead className="text-gray-400">Quantity</TableHead>
                      <TableHead className="text-gray-400">Price</TableHead>
                      <TableHead className="text-gray-400">Current Price</TableHead>
                      <TableHead className="text-gray-400">Invested Amount</TableHead>
                      <TableHead className="text-gray-400">Current Amount</TableHead>
                      <TableHead className="text-gray-400">Profit</TableHead>
                      <TableHead className="text-gray-400">Return %</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {investments.map((investment: InvestmentList) => (
                      <TableRow key={investment.id} className="border-gray-800 hover:bg-gray-900">
                        <TableCell className="text-gray-300">{investment.operation_type}</TableCell>
                        <TableCell className="text-gray-300">{investment.country}</TableCell>
                        <TableCell className="text-gray-300">{investment.negotiation_market}</TableCell>
                        <TableCell className="text-gray-300">{investment.currency}</TableCell>
                        <TableCell className="text-gray-300">{investment.ticker}</TableCell>
                        <TableCell className="text-gray-300">{investment.quantity}</TableCell>
                        <TableCell className="text-gray-300">{formatNumber(investment.price)}</TableCell>
                        <TableCell className="text-gray-300">{formatNumber(investment.current_price)}</TableCell>
                        <TableCell className="text-gray-300">{formatNumber(investment.invested_amount)}</TableCell>
                        <TableCell className="text-gray-300">{formatNumber(investment.current_amount)}</TableCell>
                        <TableCell className="text-gray-300">{formatNumber(investment.profit)}</TableCell>
                        <TableCell className="text-gray-300">{formatNumber(investment.return_percentage * 100)}%</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        )

      case 'crypto':
        return (
          <Card className="bg-[#0f1115] border-gray-800">
            <CardHeader>
              <CardTitle className="text-white">Crypto Overview</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table className="text-white">
                  <TableHeader>
                    <TableRow className="border-gray-800">
                      <TableHead className="text-gray-400">Operation</TableHead>
                      <TableHead className="text-gray-400">Currency</TableHead>
                      <TableHead className="text-gray-400">Ticker</TableHead>
                      <TableHead className="text-gray-400">Quantity</TableHead>
                      <TableHead className="text-gray-400">Price</TableHead>
                      <TableHead className="text-gray-400">Current Price</TableHead>
                      <TableHead className="text-gray-400">Invested Amount</TableHead>
                      <TableHead className="text-gray-400">Current Amount</TableHead>
                      <TableHead className="text-gray-400">Profit</TableHead>
                      <TableHead className="text-gray-400">Return %</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {cryptoInvestments.map((investment: CryptoInvestmentList) => (
                      <TableRow key={investment.id} className="border-gray-800 hover:bg-gray-900">
                        <TableCell className="text-gray-300">{investment.operation_type}</TableCell>
                        <TableCell className="text-gray-300">{investment.currency}</TableCell>
                        <TableCell className="text-gray-300">{investment.ticker}</TableCell>
                        <TableCell className="text-gray-300">{investment.quantity}</TableCell>
                        <TableCell className="text-gray-300">{formatNumber(investment.price)}</TableCell>
                        <TableCell className="text-gray-300">{formatNumber(investment.current_price)}</TableCell>
                        <TableCell className="text-gray-300">{formatNumber(investment.invested_amount)}</TableCell>
                        <TableCell className="text-gray-300">{formatNumber(investment.current_amount)}</TableCell>
                        <TableCell className="text-gray-300">{formatNumber(investment.profit)}</TableCell>
                        <TableCell className="text-gray-300">{formatNumber(investment.return_percentage * 100)}%</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        )

      case 'tesouro_direto':
        return (
          <Card className="bg-[#0f1115] border-gray-800">
            <CardHeader>
              <CardTitle className="text-white">Tesouro Direto Overview</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <div className="min-w-[1400px]">
                  <Table className="text-white w-full">
                    <TableHeader>
                      <TableRow className="border-gray-800">
                        <TableHead className="text-gray-400">Bond Type</TableHead>
                        <TableHead className="text-gray-400">Operation</TableHead>
                        <TableHead className="text-gray-400">Interest</TableHead>
                        <TableHead className="text-gray-400">Rate</TableHead>
                        <TableHead className="text-gray-400">Operation Date</TableHead>
                        <TableHead className="text-gray-400 min-w-[150px]">End Date</TableHead>
                        <TableHead className="text-gray-400">Quantity</TableHead>
                        <TableHead className="text-gray-400">Operation Price</TableHead>
                        <TableHead className="text-gray-400">Current Price</TableHead>
                        <TableHead className="text-gray-400">Buy Amount</TableHead>
                        <TableHead className="text-gray-400">Current Amount</TableHead>
                        <TableHead className="text-gray-400">Profit</TableHead>
                        <TableHead className="text-gray-400">Return %</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {tesouroDiretoInvestments.map((investment: TesouroDiretoInvestmentList) => (
                        <TableRow key={investment.id} className="border-gray-800 hover:bg-gray-900">
                          <TableCell className="text-gray-300">{investment.bond_type}</TableCell>
                          <TableCell className="text-gray-300">{investment.operation_type}</TableCell>
                          <TableCell className="text-gray-300">{investment.interest_semianual ? 'Yes' : 'No'}</TableCell>
                          <TableCell className="text-gray-300">{investment.rate}%</TableCell>
                          <TableCell className="text-gray-300">{investment.operation_date}</TableCell>
                          <TableCell className="text-gray-300 min-w-[150px]">{investment.end_date}</TableCell>
                          <TableCell className="text-gray-300">{investment.quantity}</TableCell>
                          <TableCell className="text-gray-300">{formatNumber(investment.operation_price)}</TableCell>
                          <TableCell className="text-gray-300">{formatNumber(investment.current_price)}</TableCell>
                          <TableCell className="text-gray-300">{formatNumber(investment.buy_amount)}</TableCell>
                          <TableCell className="text-gray-300">{formatNumber(investment.current_amount)}</TableCell>
                          <TableCell className="text-gray-300">{formatNumber(investment.profit)}</TableCell>
                          <TableCell className="text-gray-300">{formatNumber(investment.return_percentage * 100)}%</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </CardContent>
          </Card>
        )

      default:
        return null
    }
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex flex-col relative">
      <AnimatedBackground />
      <div className="relative z-10 flex flex-col min-h-screen">
        <TopBar 
          title="Investments Overview"
          subtitle="Track and analyze your investments"
        />
        <main className="relative z-10 p-4 sm:p-6 lg:p-8">
          <div className="max-w-[95vw] mx-auto">
            <div className="bg-black border-gray-800 shadow-xl rounded-lg mb-6 p-4">
              <div className="flex gap-6 justify-center">
                {assetTypes.map(({ type, icon: Icon, label }) => (
                  <motion.button
                    key={type}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => handleAssetTypeSelection(type)}
                    className={`
                      relative flex items-center px-8 py-4 rounded-lg transition-all
                      bg-black text-white shadow-lg
                      hover:shadow-xl group overflow-hidden w-64
                      ${selectedType === type ? 'ring-2 ring-blue-500' : ''}
                    `}
                  >
                    <div className="absolute inset-0 bg-gradient-to-r from-blue-500/20 via-purple-500/20 to-indigo-500/20 opacity-0 group-hover:opacity-100 group-active:opacity-100 transition-opacity"></div>
                    <div className="absolute inset-0 bg-gradient-to-r from-blue-500/20 via-purple-500/20 to-indigo-500/20 animate-gradient-x"></div>
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-purple-400/10 to-transparent animate-pulse"></div>
                    <div className="relative flex items-center z-10">
                      <Icon className="w-8 h-8 mr-3" />
                      <span className="text-lg font-semibold">{label}</span>
                    </div>
                  </motion.button>
                ))}
              </div>
            </div>

            {renderInvestmentTable()}
          </div>
        </main>
        <Footer />
      </div>

      <style>{`
        @keyframes gradient-x {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        .animate-gradient-x {
          animation: gradient-x 3s ease infinite;
          background-size: 400% 400%;
        }
        @keyframes pulse {
          0%, 100% { opacity: 0.1; }
          50% { opacity: 0.4; }
        }
        .animate-pulse {
          animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }
      `}</style>
    </div>
  )
}

export default InvestmentsList